import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CheckIcon from 'mdi-react/CheckIcon';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import exampleCurrency from './exampleCurrency';
import TierCardPreview from './TierCardPreview';

// Example amounts (the real ones come from each tier's payment options)
const PRICE_UPGRADE = 349;
const PAY_NOW = 67;

const STEPS = ['page', 'confirm', 'done'];

/**
 * Upgrading a tier in the app, clickable: the member's tier page with an
 * upgrade banner, the sheet with the cost spelled out, paying, and the
 * welcome. `current` and `upgrade` are { name, background } (the club's own
 * tiers when it has them).
 */
const TierUpgradeFlow = ({ current, upgrade }) => {
  const s = partial('shared');
  const config = useConfig();
  const currency = exampleCurrency(config);
  const money = (amount) => `${amount} ${currency}`;
  const [step, setStep] = useState('page');
  const payTimer = useRef(null);
  useEffect(() => () => clearTimeout(payTimer.current), []);

  const pay = () => {
    setStep('paying');
    payTimer.current = setTimeout(() => setStep('done'), 1200);
  };
  const goTo = (next) => {
    clearTimeout(payTimer.current);
    setStep(next);
  };

  const page = (
    <>
      <div className="tier-flow__stage">
        <div className="tier-flow__bar">
          <span className="tier-flow__round">‹</span>
          <span className="tier-flow__round">▦</span>
        </div>
        <TierCardPreview
          name={current.name}
          background={current.background}
          layout="flow"
        />
        <span className="tier-flow__name">{current.name}</span>
      </div>
      <div className="tier-flow__body">
        <button
          type="button"
          className="tier-flow__upsell"
          onClick={() => goTo('confirm')}
        >
          <span className="tier-flow__upsell-title">
            {s('tierFlowUpgradeTo', { tier: upgrade.name })}
          </span>
          <span className="tier-flow__upsell-price">
            {s('tierFlowPerMonth', { price: money(PRICE_UPGRADE) })}
          </span>
          <span className="tier-flow__upsell-button">
            {s('tierFlowUpgrade')}
          </span>
        </button>
        <div className="tier-flow__tabs" aria-hidden>
          <span className="tier-flow__tab--on">{s('tierPhoneIncluded')}</span>
          <span>{s('tiersProduct-offers')}</span>
          <span>{s('tiersProduct-news')}</span>
        </div>
        <div className="tier-phone__panel">
          <ul>
            <li>{s('tierPhoneIncluded1')}</li>
            <li>{s('tierPhoneIncluded2')}</li>
            <li>{s('tierPhoneIncluded3')}</li>
          </ul>
        </div>
      </div>
    </>
  );

  return (
    <div className="tier-flow">
      <div className="tier-phone tier-flow__phone">
        <div className="tier-flow__screen">
          {page}

          {step === 'confirm' && (
            <div className="tier-flow__overlay">
              <div className="tier-flow__sheet">
                <span className="tier-flow__grab" />
                <h4>{s('tierFlowUpgradeTo', { tier: upgrade.name })}</h4>
                <div className="tier-flow__option">
                  <strong>{upgrade.name}</strong>
                  <span>
                    {s('tierFlowPerMonth', { price: money(PRICE_UPGRADE) })}
                  </span>
                </div>
                <div className="tier-flow__summary">
                  <span>{s('tierFlowPayNow')}</span>
                  <strong>{money(PAY_NOW)}</strong>
                  <span>{s('tierFlowFromNext')}</span>
                  <strong>
                    {s('tierFlowPerMonth', { price: money(PRICE_UPGRADE) })}
                  </strong>
                </div>
                <button
                  type="button"
                  className="tier-flow__primary"
                  onClick={pay}
                >
                  {s('tierFlowPay', { price: money(PAY_NOW) })}
                </button>
                <button
                  type="button"
                  className="tier-flow__quiet"
                  onClick={() => goTo('page')}
                >
                  {s('tierFlowBack')}
                </button>
              </div>
            </div>
          )}

          {step === 'paying' && (
            <div className="tier-flow__overlay tier-flow__overlay--center">
              <span className="tier-flow__spin" aria-hidden />
              <span className="tier-flow__spin-text">
                {s('tierFlowPaying')}
              </span>
            </div>
          )}

          {step === 'done' && (
            <div className="tier-flow__overlay">
              <div className="tier-flow__sheet tier-flow__success">
                <span className="tier-flow__grab" />
                <span className="tier-flow__tick">
                  <CheckIcon size={26} />
                </span>
                <h4>{s('tierFlowWelcome', { tier: upgrade.name })}</h4>
                <p>{s('tierFlowWelcomeText', { price: money(PAY_NOW) })}</p>
                <div className="tier-flow__mini">
                  <TierCardPreview
                    name={upgrade.name}
                    background={upgrade.background}
                    variant="platinum"
                    layout="flow"
                  />
                </div>
                <button
                  type="button"
                  className="tier-flow__primary"
                  onClick={() => goTo('page')}
                >
                  {s('tierFlowSeeMemberships')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Jump between the steps, or start over */}
      <div className="tier-flow__steps">
        {STEPS.map((name, index) => (
          <button
            key={name}
            type="button"
            className={`tier-flow__step${
              step === name || (name === 'confirm' && step === 'paying')
                ? ' tier-flow__step--on'
                : ''
            }`}
            onClick={() => goTo(name)}
          >
            <span>{index + 1}</span>
            {s(`tierFlowStep-${name}`)}
          </button>
        ))}
      </div>
      <span className="tier-flow__note">{s('tierFlowPricesExample')}</span>
    </div>
  );
};

const tierShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  background: PropTypes.string,
});

TierUpgradeFlow.propTypes = {
  current: tierShape.isRequired,
  upgrade: tierShape.isRequired,
};

export default TierUpgradeFlow;
